import { Button, Flex, Layout, Menu, Tag, Tooltip } from 'antd'
import type { MenuProps } from 'antd'
import { PanelLeftClose, PanelLeftOpen } from 'lucide-react'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'

import type { OrganizationLong } from 'src/api/main'
import { GettingStartedPill } from 'src/features/workspaces/onboarding/checklist'
import { workspaceSideNavKeyFromPath } from 'src/router'

import type { WorkspaceMenuItem, WorkspaceMenuModel } from './hooks'
import { useStyles } from './styles'
import { WorkspaceSwitcher } from './WorkspaceSwitcher'

const { Sider } = Layout

type Props = {
  collapsed: boolean
  onCollapse: (next: boolean) => void
  menu: WorkspaceMenuModel
  workspaces: OrganizationLong[]
  activeWorkspaceId: number | undefined
}

const TAG_TONE_TO_ANTD: Record<
  NonNullable<WorkspaceMenuItem['tag']>['tone'] & string,
  string | undefined
> = {
  default: undefined,
  success: 'success',
  warning: 'warning',
  error: 'error',
  info: 'processing'
}

const renderMenuLabel = (
  item: WorkspaceMenuItem,
  comingSoonLabel: string,
  collapsed: boolean
) => {
  // Collapsed sidebar: just the label text (used for tooltip / a11y) — antd hides it visually.
  if (collapsed) {
    return item.label
  }

  const trailingTag = item.disabled
    ? { label: comingSoonLabel, tone: 'warning' as const }
    : item.tag

  if (!trailingTag) {
    return item.label
  }

  return (
    <Flex justify="space-between" align="center" style={{ width: '100%' }}>
      <span>{item.label}</span>
      <Tag
        color={TAG_TONE_TO_ANTD[trailingTag.tone ?? 'default']}
        style={{
          marginInlineEnd: 0,
          fontSize: 10,
          lineHeight: '16px',
          padding: '0 5px',
          fontWeight: 500
        }}
      >
        {trailingTag.label}
      </Tag>
    </Flex>
  )
}

export const Sidebar = ({
  collapsed,
  onCollapse,
  menu,
  workspaces,
  activeWorkspaceId
}: Props) => {
  const { styles } = useStyles()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()

  const selectedKey = workspaceSideNavKeyFromPath(location.pathname)
  const selectedKeys = useMemo(() => [selectedKey], [selectedKey])

  const hasReachableSection = menu.groups.length > 0

  const comingSoonLabel = t('shell.menu.comingSoon')

  // Flat lookup so the click handler can resolve item path without scanning groups.
  const itemIndex = useMemo(() => {
    const map = new Map<string, WorkspaceMenuItem>()
    for (const group of menu.groups) {
      for (const item of group.items) {
        map.set(item.key, item)
      }
    }
    return map
  }, [menu])

  const items: MenuProps['items'] = useMemo(
    () =>
      menu.groups.map((group) => ({
        key: `group-${group.key}`,
        type: 'group' as const,
        label: collapsed ? null : group.label,
        children: group.items.map((item) => ({
          key: item.key,
          icon: item.icon,
          label: renderMenuLabel(item, comingSoonLabel, collapsed),
          disabled: item.disabled,
          title: item.disabled
            ? `${item.label} — ${comingSoonLabel}`
            : item.label
        }))
      })),
    [menu, comingSoonLabel, collapsed]
  )

  const handleClick: MenuProps['onClick'] = ({ key }) => {
    const target = itemIndex.get(key)
    if (target && !target.disabled) {
      navigate(target.path)
    }
  }

  // The getting-started checklist points at sidebar destinations, so it asks the
  // menu for them rather than rebuilding the access-role and feature-flag rules
  // that decided what is in here.
  const resolveMenuPath = useCallback(
    (key: string) => {
      const item = itemIndex.get(key)

      return item && !item.disabled ? item.path : undefined
    },
    [itemIndex]
  )

  return (
    <Sider
      width={240}
      collapsedWidth={64}
      collapsible
      collapsed={collapsed}
      onCollapse={onCollapse}
      trigger={null}
      className={styles.sider}
      breakpoint="lg"
      data-shell-chrome="sidebar"
    >
      <div className={styles.siderTop}>
        <WorkspaceSwitcher
          collapsed={collapsed}
          workspaces={workspaces}
          activeWorkspaceId={activeWorkspaceId}
        />
      </div>

      <Menu
        mode="inline"
        selectedKeys={selectedKeys}
        items={items}
        onClick={handleClick}
        className={styles.siderMenu}
        inlineIndent={16}
      />

      <div className={styles.siderFooter}>
        {hasReachableSection ? (
          <GettingStartedPill
            collapsed={collapsed}
            workspaceId={activeWorkspaceId}
            resolvePath={resolveMenuPath}
          />
        ) : null}

        <Flex align="center" justify={collapsed ? 'center' : 'flex-end'}>
          <Tooltip
            title={
              collapsed
                ? t('shell.sidebar.expand')
                : t('shell.sidebar.collapse')
            }
            placement={collapsed ? 'right' : 'top'}
          >
            <Button
              type="text"
              size="small"
              icon={
                collapsed ? (
                  <PanelLeftOpen size={16} />
                ) : (
                  <PanelLeftClose size={16} />
                )
              }
              onClick={() => onCollapse(!collapsed)}
              aria-label={
                collapsed
                  ? t('shell.sidebar.expand')
                  : t('shell.sidebar.collapse')
              }
            />
          </Tooltip>
        </Flex>
      </div>
    </Sider>
  )
}
